import React, { useMemo } from "react";
import { AlertTriangle, ShieldCheck, ArrowRight } from "lucide-react";
import { useWizard } from "@/context/WizardContext";
import { allDhsIssues } from "@/lib/dhsRules";
import { fieldToStep, fieldToTestId } from "@/lib/fieldToStep";
import { focusMissing } from "@/lib/stepValidation";

/**
 * Inline DHS compliance banner rendered at the top of every wizard step.
 *
 * Uses the deterministic DHS engine (`allDhsIssues`) — no network roundtrip.
 * Shows only issues whose owning field lives on the CURRENT step, so nurses
 * see issues the moment they land, not when they reach Step 9.
 *
 * Behaviour:
 *   - 0 issues → renders a thin "DHS-compliant" confirmation pill
 *   - ≥1 issue → compact card listing each with scroll-to-field button
 */
export default function DhsStepBanner({ step }) {
  const { data } = useWizard();

  const stepIssues = useMemo(() => {
    const all = allDhsIssues(data);
    return all.filter((i) => {
      const t = fieldToStep(i.field);
      return t && t.step === step;
    });
  }, [data, step]);

  if (stepIssues.length === 0) {
    return (
      <div
        className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-[#B7CADA] bg-[#E7F1FB] text-[#0A3D79] text-[11.5px] font-semibold uppercase tracking-[0.1em] px-3 py-1"
        data-testid={`dhs-banner-clean-${step}`}
      >
        <ShieldCheck className="w-3.5 h-3.5" /> DHS-compliant on this step
      </div>
    );
  }

  const errorCount = stepIssues.filter((i) => i.severity === "error").length;
  const warnCount = stepIssues.length - errorCount;
  const severest = errorCount > 0 ? "error" : "warning";
  const accent = severest === "error" ? "#c05746" : "#DBB90A";
  const labelColor = severest === "error" ? "text-[#c05746]" : "text-[#8A6B00]";

  const jump = (issue) => {
    const tid = issue.testid || fieldToTestId(issue.field);
    focusMissing(tid);
  };

  return (
    <div
      className="mb-5 rounded-xl bg-white border border-[#E7EEF3] overflow-hidden relative"
      style={{ boxShadow: "0 1px 2px rgba(11,30,58,0.04)" }}
      data-testid={`dhs-banner-${step}`}
    >
      {/* Left accent rail matches severity */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: accent }}
        aria-hidden="true"
      />
      <div className="flex items-center gap-2 pl-5 pr-4 pt-3.5">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}18`, color: accent }}
        >
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
        </div>
        <span className={`text-[11.5px] uppercase tracking-[0.12em] font-semibold ${labelColor}`}>
          DHS compliance · this step
        </span>
        <span className="text-[12px] font-semibold text-[#0B1E3A]">
          {errorCount > 0 && `${errorCount} error${errorCount > 1 ? "s" : ""}`}
          {errorCount > 0 && warnCount > 0 && " · "}
          {warnCount > 0 && `${warnCount} warning${warnCount > 1 ? "s" : ""}`}
        </span>
      </div>
      <ul className="divide-y divide-[#F1F5F9] mt-2 pl-5">
        {stepIssues.map((issue, idx) => {
          const isError = issue.severity === "error";
          return (
            <li
              key={`${issue.rule || "ai"}-${issue.field}-${idx}`}
              className="flex items-start gap-3 pr-4 py-2.5"
              data-testid={`dhs-banner-${step}-issue-${idx}`}
            >
              <span
                className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  isError ? "bg-[#c05746]" : "bg-[#DBB90A]"
                }`}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0 text-[#0B1E3A]">
                <div className="flex items-center gap-2 flex-wrap">
                  {issue.rule && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.08em] bg-[#0f3b5f] text-white px-1.5 py-0.5 rounded">
                      {issue.rule}
                    </span>
                  )}
                  <span className="text-[11.5px] font-mono text-[#1E2E4A]">{issue.field}</span>
                </div>
                <div className="text-[13.5px] leading-snug mt-0.5">{issue.message}</div>
              </div>
              <button
                type="button"
                onClick={() => jump(issue)}
                className="self-center shrink-0 inline-flex items-center gap-1 rounded-md border border-[#0A3D79] bg-white hover:bg-[#E7F1FB] text-[#0A3D79] text-[11.5px] font-semibold px-2.5 py-1 transition"
                data-testid={`dhs-banner-${step}-issue-${idx}-jump`}
                title={`Scroll to ${issue.label || issue.field}`}
              >
                Jump to field <ArrowRight className="w-3 h-3" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
